import {
  buildExactMatchIndex,
  findExactMatchFromIndex,
  type ExactMatchIndex,
} from './query-get-closest-point.js';
import { getPrevailingShapeType } from '../components/overlay/utils/get-prevailing-shape-type.js';
import type {
  HoverQuery,
  HoverQueryArgs,
  HoverQueryResult,
  QueryClosest,
  QueryHovered,
} from '../context/XYChartOverlayPerformance.context.js';
import type {
  AreaSeriesWithDatapoints,
  DotSeriesWithDatapoints,
  LineSeriesWithDatapoints,
  MapDatapoints,
} from '../types/xy-chart-internals.js';

/**
 * Build the single-datapoint hover query.
 *
 * Runs closest-point queries on continuous series (dot/line/area) and
 * hovered queries on geometric series (rect/bar), then picks the
 * prevailing shape under the cursor. Every datapoint that shares the exact
 * same `x0`/`x1`/`y0`/`y1` bounding box as the prevailing one is included so
 * overlapping points are displayed together, falling back to the single
 * prevailing datapoint when nothing else matches.
 */
export function buildQuerySingle(
  queryClosest: QueryClosest,
  queryHovered: QueryHovered,
  datapointMetadataMap?: MapDatapoints,
): HoverQuery {
  let cachedDotSeriesData: DotSeriesWithDatapoints[] | undefined;
  let cachedLineSeriesData: LineSeriesWithDatapoints[] | undefined;
  let cachedAreaSeriesData: AreaSeriesWithDatapoints[] | undefined;
  let cachedIndex: ExactMatchIndex | undefined;

  return function querySingle({
    position,
    dotSeriesData,
    lineSeriesData,
    areaSeriesData,
    rectSeriesData,
    barSeriesData,
  }: HoverQueryArgs): HoverQueryResult {
    const relative = position.relative;
    const results = {
      dot: queryClosest(dotSeriesData, relative),
      line: queryClosest(lineSeriesData, relative),
      area: queryClosest(areaSeriesData, relative),
      rect: queryHovered(rectSeriesData, relative),
      bar: queryHovered(barSeriesData, relative),
    };

    const prevailingShape = getPrevailingShapeType(
      results.dot?.distance,
      results.line?.distance,
      results.area?.distance,
      !!results.rect,
      !!results.bar,
    );

    if (!prevailingShape || !results[prevailingShape]) {
      return { preferred: undefined, datapoints: [] };
    }

    if (
      cachedIndex === undefined ||
      cachedDotSeriesData !== dotSeriesData ||
      cachedLineSeriesData !== lineSeriesData ||
      cachedAreaSeriesData !== areaSeriesData
    ) {
      cachedDotSeriesData = dotSeriesData;
      cachedLineSeriesData = lineSeriesData;
      cachedAreaSeriesData = areaSeriesData;
      cachedIndex = buildExactMatchIndex(
        [...dotSeriesData, ...lineSeriesData, ...areaSeriesData],
        datapointMetadataMap,
      );
    }

    const preferred = results[prevailingShape];
    const datapoints = findExactMatchFromIndex(
      preferred,
      cachedIndex,
      datapointMetadataMap,
    );
    return { preferred, datapoints };
  };
}
